import { createSlice } from '@reduxjs/toolkit'

const slice = createSlice({
  name: 'bookCab',
  initialState: {
    // Vehicle Type
    typeListLoading: false,
    typeList: [],

    // Vehicle Category
    categoryListLoading: false,
    categoryList: [],

    // Vehicle Subcategory — Passenger only
    subCategoryListLoading: false,
    subCategoryList: [],

    // Home banner
    bannerList: [],

    // Nearby vehicles
    nearbyLoading: false,
    nearbyList: [],
    nearbyTotal: 0,
    nearbySearched: false, // true once a search has actually run
    nearbyError: '',

    // Where we are searching from. `source` is 'gps' or 'manual' — shown in the
    // UI so the user knows whether the pin came from their device or their typing.
    userLocation: null, // { latitude, longitude, label, source }

    // Current user selection
    selectedTypeId: '',
    selectedCategoryId: '',
    selectedSubCategoryId: '',

    ApiError: '',
  },
  reducers: {
    // ---------- Vehicle Type ----------
    getTypeListApiData: (state) => {
      state.typeListLoading = true
      state.ApiError = ''
    },
    getTypeListApiSuccess: (state, { payload }) => {
      state.typeListLoading = false
      state.typeList = payload?.data || []
    },
    getTypeListApiFailed: (state, { payload }) => {
      state.typeListLoading = false
      state.typeList = []
      state.ApiError = payload?.message || 'Something went wrong'
    },

    // ---------- Vehicle Category ----------
    getCategoryListApiData: (state) => {
      state.categoryListLoading = true
      state.ApiError = ''
    },
    getCategoryListApiSuccess: (state, { payload }) => {
      state.categoryListLoading = false
      state.categoryList = payload?.data || []
    },
    getCategoryListApiFailed: (state, { payload }) => {
      state.categoryListLoading = false
      state.categoryList = []
      state.ApiError = payload?.message || 'Something went wrong'
    },

    // ---------- Vehicle Subcategory ----------
    getSubCategoryListApiData: (state) => {
      state.subCategoryListLoading = true
      state.ApiError = ''
    },
    getSubCategoryListApiSuccess: (state, { payload }) => {
      state.subCategoryListLoading = false
      state.subCategoryList = payload?.data || []
    },
    getSubCategoryListApiFailed: (state, { payload }) => {
      state.subCategoryListLoading = false
      state.subCategoryList = []
      state.ApiError = payload?.message || 'Something went wrong'
    },

    // ---------- Selection ----------
    // Changing a step invalidates everything below it in the cascade, so the
    // child selections AND their stale lists are cleared here. Clearing the
    // lists matters as much as the ids — otherwise the previous type's cards
    // stay on screen until the new request lands.
    setSelectedTypeId: (state, { payload }) => {
      state.selectedTypeId = payload
      state.selectedCategoryId = ''
      state.selectedSubCategoryId = ''
      state.categoryList = []
      state.subCategoryList = []
    },
    setSelectedCategoryId: (state, { payload }) => {
      state.selectedCategoryId = payload
      state.selectedSubCategoryId = ''
      state.subCategoryList = []
    },
    setSelectedSubCategoryId: (state, { payload }) => {
      state.selectedSubCategoryId = payload
    },

    // ---------- Banner ----------
    getBannerApiData: () => {},
    getBannerApiSuccess: (state, { payload }) => {
      // One banner row carries MANY images in `banner_path` (an array of
      // {name, path}, despite the singular name). Flatten every row's images
      // into one list of URLs — that is what the slider actually needs.
      state.bannerList = (payload?.data || [])
        .flatMap((b) => b?.banner_path || [])
        .map((f) => f?.path)
        .filter(Boolean)
    },
    getBannerApiFailed: (state) => {
      state.bannerList = []
    },

    // ---------- Nearby vehicles ----------
    setUserLocation: (state, { payload }) => {
      state.userLocation = payload
    },
    getNearbyApiData: (state) => {
      state.nearbyLoading = true
      state.nearbyError = ''
    },
    getNearbyApiSuccess: (state, { payload }) => {
      state.nearbyLoading = false
      state.nearbyList = payload?.data || []
      state.nearbyTotal = payload?.total || 0
      state.nearbySearched = true
    },
    getNearbyApiFailed: (state, { payload }) => {
      state.nearbyLoading = false
      state.nearbyList = []
      state.nearbyTotal = 0
      state.nearbySearched = true
      state.nearbyError = payload?.message || 'Could not load nearby vehicles'
    },
    clearNearby: (state) => {
      state.nearbyList = []
      state.nearbyTotal = 0
      state.nearbySearched = false
      state.nearbyError = ''
    },
  },
})

export const {
  getTypeListApiData,
  getTypeListApiSuccess,
  getTypeListApiFailed,
  getCategoryListApiData,
  getCategoryListApiSuccess,
  getCategoryListApiFailed,
  getSubCategoryListApiData,
  getSubCategoryListApiSuccess,
  getSubCategoryListApiFailed,
  setSelectedTypeId,
  setSelectedCategoryId,
  setSelectedSubCategoryId,
  getBannerApiData,
  getBannerApiSuccess,
  getBannerApiFailed,
  setUserLocation,
  getNearbyApiData,
  getNearbyApiSuccess,
  getNearbyApiFailed,
  clearNearby,
} = slice.actions

export default slice.reducer
